import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { osStore } from "../os/store";
import { SearchPicker, type PickerItem } from "./SearchPicker";
import type { ModuleRecord } from "@arclog/module-sdk";

type Hit = PickerItem & { version?: string };

function usePackages(): ModuleRecord[] {
  const [packages, setPackages] = useState(osStore.packages);
  useEffect(() => osStore.subscribe(() => setPackages(osStore.packages)), []);
  return packages;
}

/**
 * Spotlight-style app finder on the top menubar (magnifying glass).
 * Opens with Ctrl+Alt+Space (or Ctrl/Cmd+K). Avoids macOS Cmd+Space.
 */
export function SpotlightSearch() {
  const packages = usePackages();
  const [open, setOpen] = useState(false);

  // Filtering/ranking lives in SearchPicker; this just supplies the candidates.
  const hits: Hit[] = useMemo(
    () =>
      packages
        .filter((p) => p.manifest?.kind === "app")
        .map((p) => ({
          id: p.manifest.id || p.id,
          name: p.manifest.name || p.id,
          version: p.defaultVersion,
          icon: p.manifest.dock?.icon,
          subtitle: `${p.manifest.id || p.id}${
            p.defaultVersion ? " · v" + p.defaultVersion : ""
          }${p.manifest.core ? " · core" : ""}`,
        })),
    [packages]
  );

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      // Primary: Ctrl+Alt+Space / Ctrl+Alt+K (avoids macOS Cmd+Space)
      const launcher =
        e.ctrlKey &&
        e.altKey &&
        !e.metaKey &&
        (e.code === "Space" || e.key.toLowerCase() === "k" || e.code === "KeyK");
      // Secondary: Ctrl/Cmd+K only (not Space)
      const legacy =
        (e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k";
      if (launcher || legacy) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className="icon-btn spotlight-btn"
        title="Search apps (Ctrl/Cmd+Space)"
        aria-label="Search apps"
        onClick={() => setOpen(true)}
      >
        <Search size={14} strokeWidth={2} />
      </button>

      <SearchPicker
        open={open}
        items={hits}
        placeholder="Search apps…"
        emptyText="No apps match"
        ariaLabel="Search apps"
        footer="Up/Down navigate · Enter open · Esc close · Ctrl/Cmd+Space"
        onPick={(h) => osStore.openApp(h.id, h.name, (h as Hit).version)}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
